import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import UserAccountMenu from '../../components/ui/UserAccountMenu';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PostOfTheWeek from './components/PostOfTheWeek';
import ReadyToShareGrid from './components/ReadyToShareGrid';
import QuickNavigationCards from './components/QuickNavigationCards';
import MarketingSupportCTA from './components/MarketingSupportCTA';
import ActivitySummary from './components/ActivitySummary';
import Icon from '../../components/AppIcon';
import { dashboardContentService } from '../../services/dashboardContentService';

const ProvidersDashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [copySuccess, setCopySuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [postOfTheWeek, setPostOfTheWeek] = useState(null);
  const [readyToShareItems, setReadyToShareItems] = useState([]);

  useEffect(() => {
    loadDashboardContent();
  }, []);

  const loadDashboardContent = async () => {
    try {
      setLoading(true);
      const [post, items] = await Promise.all([
        dashboardContentService?.getPostOfWeek(),
        dashboardContentService?.getReadyToShareItems()
      ]);
      setPostOfTheWeek(post);
      setReadyToShareItems(items);
    } catch (error) {
      console.error('Failed to load dashboard content:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickNavCards = [
  {
    id: 2,
    title: "Asset Library",
    description: "Download logos, images, videos, and approved marketing materials",
    icon: "FolderOpen",
    path: "/asset-library",
    count: "200+"
  },
  {
    id: 3,
    title: "Content Calendar",
    description: "View year-round content planning with monthly themes and post ideas",
    icon: "Calendar",
    path: "/content-calendar"
  },
  {
    id: 4,
    title: "Email Campaigns",
    description: "Ready-to-use email templates for various campaign types",
    icon: "Mail",
    path: "/email-campaigns",
    count: "25+"
  },
  {
    id: 5,
    title: "Press Release Template",
    description: "Generate professional press releases with fill-in-the-blank format",
    icon: "FileText",
    path: "/press-release-template"
  },
  {
    id: 6,
    title: "Best Practices",
    description: "Social media filming guidelines and content creation framework",
    icon: "Lightbulb",
    path: "/best-practices"
  }];


  const activityMetrics = [
  {
    id: 1,
    label: "Content Downloaded",
    value: "24",
    icon: "Download",
    iconColor: "black",
    bgColor: "corvid",
    change: "+8 this week",
    changeType: "increase"
  },
  {
    id: 2,
    label: "Captions Copied",
    value: "42",
    icon: "Copy",
    iconColor: "var(--color-accent)",
    bgColor: "bg-accent/10",
    change: "+12 this week",
    changeType: "increase"
  },
  {
    id: 3,
    label: "Available Posts",
    value: "150+",
    icon: "FileText",
    iconColor: "var(--color-success)",
    bgColor: "bg-success/10"
  },
  {
    id: 4,
    label: "New This Week",
    value: "8",
    icon: "Sparkles",
    iconColor: "var(--color-warning)",
    bgColor: "bg-warning/10",
    change: "Fresh content",
    changeType: "neutral"
  }];


  const handleCopy = async (text) => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleDownload = async (imageUrl, title) => {
    try {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `${title?.replace(/\s+/g, '-')?.toLowerCase()}.jpg`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleCopyCaption = async (caption) => {
    await handleCopy(caption);
  };

  const handleDownloadImage = async (imageUrl, title) => {
    await handleDownload(imageUrl, title);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div className="lg:ml-60">
        <header className="sticky top-0 z-40 bg-card border-b border-border shadow-sm">
          <div className="flex items-center justify-between px-4 md:px-6 py-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Provider Dashboard</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1 font-caption">
                Your central hub for marketing content and resources
              </p>
            </div>
            <div className="flex items-center gap-3">
              {!isAuthenticated &&
              <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-primary-foreground transition-colors duration-200">

                  <Icon name="Lock" size={16} />
                  Admin Login
                </button>
              }
              <UserAccountMenu />
            </div>
          </div>
        </header>

        <main className="p-4 md:p-6 lg:p-8 space-y-6 md:space-y-8">
          {copySuccess && (
            <div className="fixed top-4 right-4 bg-success text-success-foreground px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2">
              <Icon name="Check" size={18} />
              <span className="font-medium">Copied to clipboard!</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader" size={32} className="animate-spin text-primary" />
            </div>
          ) : (
            <>
              {postOfTheWeek && (
                <PostOfTheWeek
                  post={postOfTheWeek}
                  onCopy={handleCopyCaption}
                  onDownload={handleDownloadImage}
                />
              )}

              {readyToShareItems?.length > 0 && (
                <ReadyToShareGrid
                  items={readyToShareItems}
                  onCopy={handleCopyCaption}
                  onDownload={handleDownloadImage}
                />
              )}
            </>
          )}

          <MarketingSupportCTA />

          <QuickNavigationCards cards={quickNavCards} />

          <ActivitySummary metrics={activityMetrics} />
        </main>
      </div>
    </div>);

};

export default ProvidersDashboard;